import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.97) translateY(6px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 16px;
`;

export const Sheet = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 620px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
  animation: ${fadeIn} 0.16s ease;
  overflow: hidden;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

export const Header = styled.div`
  padding: 20px 20px 0;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.01em;
  }
`;

export const CloseBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1.5px solid #e5e7eb;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`;

export const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 12px;
  background: #ffffff;
  margin-bottom: 20px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: #006633;
    box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.1);
  }

  svg {
    color: #9ca3af;
    flex-shrink: 0;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  height: 40px;
  border: none;
  outline: none;
  font-size: 13.5px;
  font-family: inherit;
  color: #111827;
  background: transparent;

  &::placeholder {
    color: #9ca3af;
  }
`;

export const Divider = styled.div`
  height: 1px;
  background: #f3f4f6;
`;

export const SectionLabel = styled.div`
  padding: 12px 20px 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: #9ca3af;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 8px 8px;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 4px;
  }
`;

export const Arrow = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: #111827;
  transition: color 0.15s ease;
`;

export const Item = styled.li<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#f0faf4' : 'transparent')};
  transition: background 0.1s ease;

  ${Arrow} {
    color: ${({ $selected }) => ($selected ? '#006633' : '#111827')};
  }

  &:hover {
    background: ${({ $selected }) => ($selected ? '#f0faf4' : '#f9fafb')};

    ${Arrow} {
      color: #006633;
    }
  }
`;

export const Avatar = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  background: ${({ $color }) => $color};
  text-transform: uppercase;
`;

export const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const Name = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Sub = styled.span`
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Empty = styled.div`
  text-align: center;
  padding: 36px 16px;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 500;

  svg {
    display: block;
    margin: 0 auto 10px;
    color: #d1d5db;
  }
`;

export const Footer = styled.div`
  padding: 12px 20px 16px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const CountLabel = styled.span`
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
`;

export const ConfirmBtn = styled.button`
  height: 36px;
  padding: 0 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  background: #006633;
  color: #ffffff;
  border: none;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #004d26;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
